import { Grid, makeStyles, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { BasicUserUpdate } from "../interfaces/BasicUserUpdate";

const useStyles = makeStyles((theme) => ({
  input: {
    marginBottom: "20px",
    border: `2px solid #fee9ee`,
    borderRadius: "12px",
    height: "52px",
    transition: "background .25s, border .25s",
    background: theme.palette.background.default,
    "& .MuiAutocomplete-popupIndicatorOpen:selection": {
      background: "transparent",
    },
    "& .MuiInputBase-root.MuiAutocomplete-inputRoot > .MuiAutocomplete-input": {
      padding: "22px 12px 10px !important",
    },
    "& .MuiInputBase-root.MuiAutocomplete-inputRoot": {
      padding: "0px !important",
    },
    "&.MuiAutocomplete-root:has(.Mui-focused)": {
      border: "2px solid #FD9BB5",
      background: "#fee9ee",
    },
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& .MuiFilledInput-root": {
      background: "none",
      height: "46px",
      "&.Mui-focused > div": {
        background: `${theme.palette.primary.main}50`,
      },
    },
    "& .MuiFilledInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiFilledInput-underline:before": {
      borderBottom: "none",
    },
  },
  middleButton: {
    paddingLeft: "4px",
    paddingRight: "4px",
  },
  leftButton: {
    paddingRight: "4px",
  },
  rightButton: {
    paddingLeft: "4px",
  },
  title: {
    marginBottom: "8px",
  },
}));

interface Props {
  translations: {
    title: string;
    year: string;
    month: string;
    day: string;
    months: {
      january: string;
      february: string;
      march: string;
      april: string;
      may: string;
      june: string;
      july: string;
      august: string;
      september: string;
      october: string;
      november: string;
      december: string;
    };
  };
  defaultDate: Date;
  setBasicUserData: (data: Partial<BasicUserUpdate>) => void;
  loading: boolean;
}

export const AgeInput: React.FC<Props> = (props) => {
  const { defaultDate, setBasicUserData, translations, loading } = props;
  const classes = useStyles();
  const [yearInput, setYearInput] = useState<number>(
    defaultDate ? new Date(defaultDate).getFullYear() : null
  );
  const [monthInput, setMonthInput] = useState<number>(
    defaultDate ? new Date(defaultDate).getMonth() + 1 : null
  );
  const [dayArray, setDayArray] = useState<number[]>([]);
  const [dayInput, setDayInput] = useState<number>(
    defaultDate ? new Date(defaultDate).getDate() : null
  );

  useEffect(() => {
    if (yearInput && monthInput) {
      const daysInMonth = new Date(yearInput, monthInput, 0).getDate();
      const array: number[] = [];

      for (let i = 1; i <= daysInMonth; i++) {
        array.push(i);
      }

      setDayArray(array);
    }

    if (yearInput && monthInput && dayInput) {
      setBasicUserData({
        dateOfBirth: new Date(yearInput, monthInput - 1, dayInput, 12),
      });
    } else {
      setBasicUserData({
        dateOfBirth: null,
      });
    }
  }, [yearInput, monthInput, dayInput]);

  const yearArray = () => {
    const yearNow = new Date(Date.now()).getFullYear() - 13;
    const array: number[] = [];

    for (let i = yearNow; i > yearNow - 110 + 13; i--) {
      array.push(i);
    }

    return array;
  };

  const monthArray = [
    { date: 1, label: translations.months.january },
    { date: 2, label: translations.months.february },
    { date: 3, label: translations.months.march },
    { date: 4, label: translations.months.april },
    { date: 5, label: translations.months.may },
    { date: 6, label: translations.months.june },
    { date: 7, label: translations.months.july },
    { date: 8, label: translations.months.august },
    { date: 9, label: translations.months.september },
    { date: 10, label: translations.months.october },
    { date: 11, label: translations.months.november },
    { date: 12, label: translations.months.december },
  ];

  const getDefaultMonth = (date) => {
    if (date) {
      return monthArray.find((month) => month.date === date);
    }

    return null;
  };

  return (
    <Grid container direction="column">
      <Grid item xs={12}>
        <Typography variant="h6" className={classes.title}>
          {translations.title}
        </Typography>
      </Grid>
      <Grid container direction="row">
        <Grid item xs={4} className={classes.leftButton}>
          <Autocomplete
            disabled={loading}
            disableClearable
            className={classes.input}
            onChange={(_, value) =>
              value ? setYearInput(value as number) : setYearInput(null)
            }
            getOptionLabel={(year) => `${year}`}
            options={yearArray()}
            defaultValue={yearInput && yearInput}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translations.year}
                required
                fullWidth
                variant="filled"
                type="number"
              />
            )}
          />
        </Grid>
        <Grid item xs={4} className={classes.middleButton}>
          <Autocomplete
            disableClearable
            className={classes.input}
            onChange={(_, value) =>
              value
                ? setMonthInput((value as { date: number; label: string }).date)
                : setMonthInput(null)
            }
            disabled={(!monthInput && !yearInput) || loading}
            getOptionLabel={(month) => month.label}
            options={monthArray}
            defaultValue={monthInput && getDefaultMonth(monthInput)}
            renderInput={(params) => (
              <TextField
                label={translations.month}
                required
                {...params}
                fullWidth
                variant="filled"
              />
            )}
          />
        </Grid>
        <Grid item xs={4} className={classes.middleButton}>
          <Autocomplete
            disableClearable
            className={classes.input}
            onChange={(_, value) =>
              value ? setDayInput(value as number) : setDayInput(null)
            }
            disabled={(!dayInput && !monthInput) || loading}
            getOptionLabel={(day) => `${day}`}
            options={dayArray}
            defaultValue={dayInput && dayInput}
            renderInput={(params) => (
              <TextField
                label={translations.day}
                required
                {...params}
                fullWidth
                variant="filled"
                type="number"
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
